/* LekkerSeal custom cart drawer
 * Replaces UpCart. Pure Shopify AJAX API, no app dependency.
 * Public API: window.lsCart.open() / .close() / .refresh() / .add(id, qty)
 * Companion: assets/ls-cart-bridge.js (third-party overlays + analytics)
 *
 * v2 changes, all aimed at the first three seconds after Add to Cart:
 *   1. The drawer opens INSTANTLY with a skeleton, before the network call.
 *   2. The Checkout button node is never destroyed. renderFooter() builds the
 *      footer once and then patches text in place. Replacing it via innerHTML
 *      could drop a tap: if the node is swapped between pointerdown and click,
 *      the browser retargets to an element with no [data-act] and the handler
 *      silently exits.
 *   3. state.error is cleared on every successful refresh.
 *
 * v5.1 changes, aimed at the gap measured against UpCart and The Oodie:
 *   4. The Checkout button exists on the SAME TICK as the tap, with no amount
 *      on it. Measured: UpCart tappable at 70ms, The Oodie at 183ms, us at
 *      1456ms. No price is displayed before the server supplies it.
 *   5. Delivery Protection is folded into the SAME /cart/add.js call as the
 *      product. Four round trips became two.
 *   6. Tapping Checkout while the add is in flight waits for it to land.
 *
 * v5.2 changes, both about the free shipping threshold telling the truth:
 *   7. The threshold measures the cart total AFTER discounts, which is what
 *      Shopify itself uses. See shippingQualifyingTotal().
 *   8. The unlocked state states what was saved.
 *
 * v5.3 change, about the cart showing the saving that actually exists:
 *   9. Compare-at (RRP) pricing. The drawer previously derived its
 *      strike-through and its "You save" row from original_line_price, which
 *      is Shopify's PRE-DISCOUNT figure, not the RRP. While an automatic
 *      discount happened to be running the two coincided and it looked
 *      correct. With the discount off they are equal, so a Refill Pack at
 *      R999 against a R1,185 RRP showed no strike-through and no saving at
 *      all. See compareAtUnit() and lineWas().
 *
 * v5.4 changes, both presentational:
 *  10. A Shipping row that only ever appears to say FREE, once the threshold
 *      is met. Below the threshold it is hidden rather than quoting R99, so
 *      the summary never introduces a cost the customer has already avoided.
 *  11. Tighter line items - current price on the title line, RRP beside the
 *      quantity pill, Remove centred between them. See renderItem().
 */
(function () {
  var C = window.LS_CART_CONFIG;
  if (!C) return;

  var TIMEOUT_MS = 8000;

  /* Standard domestic shipping, in cents, as it appears in the Shopify
     delivery profile:
       Standard Door to Door Courier  R99   TOTAL_PRICE R0 - R999.99
       FREE Door to Door Courier      R0    TOTAL_PRICE R1,000+
     This number is shown to the customer as money they saved, so it must not
     be a guess. If the rate in Settings > Shipping changes, change it here,
     or set standardShippingCost on LS_CART_CONFIG to override it. Setting it
     to 0 removes the saving line without touching anything else. */
  var STANDARD_SHIPPING = (C.standardShippingCost != null) ? C.standardShippingCost : 9900;

  var CMP_KEY = 'ls_cmp';
  var COMPARE = window.LS_CART_COMPARE || {};
  var compareFetching = {};

  var state = { cart: null, busy: false, open: false, error: null, pending: false };
  var autoProtTried = false;
  var protAttempts = 0;
  var protInfoOpen = false;
  var inflight = null;
  var dw, ov, body, foot, meter, count;

  /* ---------- helpers ---------- */

  function money(cents) {
    var n = Math.round(cents) / 100;
    var str = n % 1 === 0 ? String(n) : n.toFixed(2);
    var p = str.split('.');
    p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return 'R' + p.join('.');
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function thumb(url, size) {
    if (!url) return '';
    var parts = String(url).split('?');
    var q = (parts[1] || '').split('&').filter(function (p) {
      return p && p.indexOf('width=') !== 0;
    });
    q.push('width=' + (size || 160));
    return parts[0] + '?' + q.join('&');
  }

  function icon(id, cls) {
    return '<svg class="' + (cls || '') + '" viewBox="0 0 24 24"><use href="#ls-i-' + id + '"></use></svg>';
  }

  function readError(r) {
    return r.text().then(function (t) {
      var msg = 'Something went wrong. Please try again.';
      try {
        var j = JSON.parse(t);
        msg = j.description || j.message || msg;
      } catch (e) { /* not JSON */ }
      return msg;
    });
  }

  function api(path, opts) {
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, TIMEOUT_MS) : null;
    var init = Object.assign({ credentials: 'same-origin' }, opts || {});
    if (ctl) init.signal = ctl.signal;

    return fetch(path, init)
      .then(function (r) {
        if (!r.ok) {
          return readError(r).then(function (m) {
            var e = new Error(m);
            e.userMessage = m;
            throw e;
          });
        }
        return r.json();
      })
      .catch(function (e) {
        if (e && e.name === 'AbortError') {
          var t = new Error('timeout');
          t.userMessage = 'That took too long. Please check your connection and try again.';
          throw t;
        }
        throw e;
      })
      .finally(function () { if (timer) clearTimeout(timer); });
  }

  function jsonPost(path, data) {
    return api(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    });
  }

  /* Faithfully converts a Dawn product form into an add.js line item, so the
     product and Delivery Protection can travel in one request. Line item
     properties matter here: the free gift carries _source, and losing it would
     break isGift() and turn the gift into a R190 charge. */
  function formToItem(form) {
    var fd = new FormData(form);
    var item = {
      id: Number(fd.get('id')),
      quantity: parseInt(fd.get('quantity') || '1', 10) || 1
    };
    var props = {};
    var any = false;
    fd.forEach(function (v, k) {
      var m = /^properties\[(.+)\]$/.exec(k);
      if (m) { props[m[1]] = v; any = true; }
    });
    if (any) item.properties = props;
    var sp = fd.get('selling_plan');
    if (sp) item.selling_plan = Number(sp);
    return item;
  }

  /* ---------- compare-at (RRP) ----------

     Shopify's AJAX cart does not return compare_at_price on line items. It
     returns original_line_price, which is the line BEFORE discounts - a
     different number that only coincides with the RRP while a discount
     happens to be running. Relying on it meant every saving vanished from the
     cart the moment a sale ended.

     Values come from three places, cheapest first:
       1. the line item itself, if a future Shopify version ever exposes it
       2. LS_CART_COMPARE, seeded from Liquid for cart items and for the
          product on the current page
       3. /products/{handle}.js, fetched once per unknown product, after the
          drawer has already painted, and cached for the session
     A stored 0 means "checked, no RRP", so it is never looked up twice. */

  function loadCompareCache() {
    try {
      var cached = JSON.parse(sessionStorage.getItem(CMP_KEY) || '{}');
      Object.keys(cached).forEach(function (k) {
        if (COMPARE[k] === undefined) COMPARE[k] = cached[k];
      });
    } catch (e) { /* private mode, or corrupt - ignore */ }
  }

  function saveCompareCache() {
    try { sessionStorage.setItem(CMP_KEY, JSON.stringify(COMPARE)); } catch (e) {}
  }

  function compareAtUnit(item) {
    if (item.compare_at_price != null && item.compare_at_price > item.final_price) {
      return item.compare_at_price;
    }
    var v = COMPARE[String(item.variant_id)];
    return (typeof v === 'number' && v > 0) ? v : 0;
  }

  /* What this line was worth before any saving: the higher of Shopify's
     pre-discount figure and the RRP. Taking the max means a product that is
     BOTH marked down from RRP and inside an active discount shows the full
     distance travelled, and neither mechanism can quietly cancel the other. */
  function lineWas(item) {
    var rrp = compareAtUnit(item) * item.quantity;
    var pre = item.original_line_price || item.final_line_price;
    return Math.max(pre, rrp);
  }

  function rrpSubtotal() {
    if (!state.cart) return 0;
    return state.cart.items.reduce(function (s, i) { return s + lineWas(i); }, 0);
  }

  /* Fired after the cart renders, never before - this must not delay paint. */
  function fillCompareGaps() {
    if (!state.cart) return;
    var handles = [];
    state.cart.items.forEach(function (i) {
      if (!i.handle) return;
      if (COMPARE[String(i.variant_id)] !== undefined) return;
      if (compareFetching[i.handle]) return;
      if (handles.indexOf(i.handle) === -1) handles.push(i.handle);
    });
    if (!handles.length) return;

    handles.forEach(function (h) {
      compareFetching[h] = true;
      fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.variants) return;
          var changed = false;
          p.variants.forEach(function (v) {
            var c = (v.compare_at_price && v.compare_at_price > v.price) ? v.compare_at_price : 0;
            if (COMPARE[String(v.id)] !== c) { COMPARE[String(v.id)] = c; changed = true; }
          });
          saveCompareCache();
          if (changed && state.open && state.cart) render();
        })
        .catch(function () { /* pricing detail only - never surface this */ });
    });
  }

  /* ---------- cart state ---------- */

  function giftCodeApplied() {
    if (!state.cart || !C.gift || !C.gift.code) return false;
    return (state.cart.discount_codes || []).some(function (d) {
      return String(d.code).toUpperCase() === String(C.gift.code).toUpperCase()
        && d.applicable !== false;
    });
  }

  function isGift(item) {
    if (!C.gift || !C.gift.variantId) return false;
    if (Number(item.variant_id) !== Number(C.gift.variantId)) return false;
    if (!item.properties || !(item.properties._source || item.properties._gift)) return false;
    if (!giftCodeApplied()) return false;
    return item.final_line_price === 0;
  }

  function isProtection(item) {
    return C.protection.variantId && Number(item.variant_id) === Number(C.protection.variantId);
  }

  function hasVariant(id) {
    if (!state.cart || !id) return false;
    return state.cart.items.some(function (i) { return Number(i.variant_id) === Number(id); });
  }

  function protectionLine() {
    if (!state.cart) return null;
    for (var i = 0; i < state.cart.items.length; i++) {
      if (isProtection(state.cart.items[i])) return state.cart.items[i];
    }
    return null;
  }

  function isGiftOnly() {
    if (!state.cart || !state.cart.items.length) return false;
    var real = state.cart.items.filter(function (i) { return !isProtection(i); });
    if (!real.length) return false;
    return real.every(isGift);
  }

  /* ============================================================
     What counts toward free shipping.

     Shopify decides this itself, from the delivery profile:
       FREE Door to Door Courier applies when TOTAL_PRICE >= R1,000
     and TOTAL_PRICE is the cart total AFTER discounts.

     This used to sum line prices, which is the total BEFORE order-level
     discount codes. So a R1,050 cart with LEKKER200 on it showed "Free
     shipping unlocked" in the drawer, then Shopify charged R99 at checkout
     because as far as it was concerned the cart was R850.

     cart.total_price is the same figure Shopify tests, so the drawer and the
     checkout can no longer disagree.
     ============================================================ */
  function shippingQualifyingTotal() {
    if (!state.cart) return 0;
    return state.cart.total_price;
  }

  function hasRealItems() {
    if (!state.cart) return false;
    return state.cart.items.some(function (i) { return !isProtection(i) && !isGift(i); });
  }

  function shippingGap() {
    if (!C.freeShippingThreshold) return 0;
    return Math.max(0, C.freeShippingThreshold - shippingQualifyingTotal());
  }

  /* ---------- delivery protection ---------- */

  var OPT_KEY = 'ls_prot_off';

  function protOptedOut() {
    try { return sessionStorage.getItem(OPT_KEY) === '1'; } catch (e) { return false; }
  }

  function setProtOptOut(off) {
    try {
      if (off) sessionStorage.setItem(OPT_KEY, '1');
      else sessionStorage.removeItem(OPT_KEY);
    } catch (e) { /* private mode */ }
  }

  function resetProtectionState() {
    autoProtTried = false;
    protAttempts = 0;
  }

  function wantsProtectionNow() {
    if (!C.protection || !C.protection.variantId) return false;
    if (C.protection.defaultOn === false) return false;
    if (protOptedOut()) return false;
    if (protectionLine()) return false;
    return true;
  }

  function setProtection(on) {
    if (!C.protection.variantId) return Promise.resolve();
    var line = protectionLine();

    if (!on) {
      if (!line) return Promise.resolve();
      return mutate(function () {
        return jsonPost(C.routes.cartChange, { id: line.key, quantity: 0 });
      });
    }
    if (line) {
      if (line.quantity === 1) return Promise.resolve();
      return mutate(function () {
        return jsonPost(C.routes.cartChange, { id: line.key, quantity: 1 });
      });
    }
    return mutate(function () {
      return jsonPost(C.routes.cartAdd, {
        items: [{ id: Number(C.protection.variantId), quantity: 1 }]
      });
    });
  }

  /* Reconciliation only. The common path now adds protection inside the same
     request as the product, so on a normal add this finds the line already
     present and does nothing. */
  function maybeAutoProtection() {
    if (!C.protection.variantId) return;
    if (C.protection.defaultOn === false) return;
    if (state.busy) return;
    // Never start a mutation while a message is on screen: mutate() clears
    // state.error on entry, which used to wipe Shopify's own stock warning
    // about a second after it appeared.
    if (state.error) return;
    if (protAttempts >= 3) return;

    var line = protectionLine();

    if (line && !hasRealItems()) { protAttempts++; setProtection(false); return; }
    if (line && line.quantity > 1) { protAttempts++; setProtection(true); return; }
    if (line) { autoProtTried = false; protAttempts = 0; return; }

    if (!state.open) return;
    if (autoProtTried || protOptedOut()) return;
    if (!hasRealItems()) return;

    autoProtTried = true;
    protAttempts++;
    setProtection(true).then(function () {
      if (!protectionLine()) autoProtTried = false;
    });
  }

  /* ---------- upsell rules ---------- */

  function cartKinds() {
    var k = { device: false, bags: false, bundle: false };
    if (!state.cart) return k;
    state.cart.items.forEach(function (i) {
      if (isGift(i) || isProtection(i)) return;
      var meta = C.catalogue[i.handle];
      if (meta && k.hasOwnProperty(meta.kind)) k[meta.kind] = true;
    });
    return k;
  }

  function pickUpsells(max) {
    var k = cartKinds();

    var rules = [
      { when: !k.device && !k.bags && !k.bundle, slate: ['camping', 'ultimate', 'pro'] },
      { when: k.bags && !k.device && !k.bundle,  slate: ['lg10', 'pro'] },
      { when: k.device && !k.bags,               slate: ['lg10', 'refill'] },
      { when: k.bundle,                          slate: ['xl10', 'refill'] },
      { when: k.device && k.bags,                slate: ['md10', 'refill'] },
      { when: true,                              slate: ['xl10', 'refill'] }
    ];

    var slate = [];
    for (var i = 0; i < rules.length; i++) {
      if (rules[i].when) { slate = rules[i].slate; break; }
    }

    var out = [];
    var taken = {};

    function consider(key) {
      if (out.length >= max || taken[key]) return;
      var p = C.offers[key];
      if (!p || !p.variantId || hasVariant(p.variantId)) return;
      if ((key === 'pro' || key === 'camping' || key === 'ultimate') && (k.device || k.bundle)) return;
      taken[key] = true;
      out.push(p);
    }

    slate.forEach(consider);
    if (out.length < max) Object.keys(C.offers).forEach(consider);
    return out;
  }

  /* ---------- render ---------- */

  function renderError() {
    if (!state.error) return '';
    return '<div class="ls-err">' + esc(state.error) + '</div>';
  }

  function skeletonRow(a, b) {
    return '<div class="ls-sk"><div class="ls-sk-th"></div><div class="ls-sk-l">' +
      '<div class="ls-sk-b" style="width:' + (a || 70) + '%"></div>' +
      '<div class="ls-sk-b" style="width:' + (b || 40) + '%"></div>' +
    '</div></div>';
  }

  function renderPending() {
    meter.innerHTML = '';
    body.innerHTML = renderError() + skeletonRow(70, 40) + skeletonRow(55, 30);
    foot.innerHTML = '<div class="ls-sk-cta"></div>';
  }

  /* Used on Add to Cart. Keeps whatever is already in the cart on screen,
     adds one shimmer row for the item on its way, and paints a real, tappable
     Checkout button immediately. */
  function renderAdding() {
    var has = !!(state.cart && state.cart.items.length);
    var html = renderError();
    if (has) {
      state.cart.items.forEach(function (i) {
        if (isProtection(i)) return;
        html += isGift(i) ? renderGift(i) : renderItem(i);
      });
    }
    html += skeletonRow(70, 40);
    body.innerHTML = html;

    if (has) renderMeter(); else meter.innerHTML = '';
    renderInstantFooter();
  }

  /* A footer with a working Checkout button and deliberately no figures. */
  function renderInstantFooter() {
    if (!foot.querySelector('[data-act="checkout"]')) buildFooterSkeleton();

    var protSlot = foot.querySelector('[data-f="prot"]');
    if (protSlot && !protSlot.innerHTML) protSlot.innerHTML = protectionHTML();

    var sums = foot.querySelector('.ls-sums');
    if (sums) sums.style.display = 'none';

    var saveRow = foot.querySelector('[data-f="saverow"]');
    if (saveRow) saveRow.hidden = true;
    var shipRow = foot.querySelector('[data-f="shiprow"]');
    if (shipRow) shipRow.hidden = true;

    var cta = foot.querySelector('[data-f="cta"]');
    if (cta && cta.textContent !== ' Checkout') cta.textContent = ' Checkout';
  }

  function renderMeter() {
    var goal = C.freeShippingThreshold;
    if (!goal) { meter.innerHTML = ''; meter.style.display = 'none'; return; }
    meter.style.display = '';
    var gap = shippingGap();
    var done = gap === 0;
    var pct = done ? 100 : Math.max(3, Math.round((shippingQualifyingTotal() / goal) * 100));

    var txt = done
      ? '<span>Free shipping unlocked</span>'
      : "You're <b>" + money(gap) + '</b> away from <b>free shipping</b>';

    var saved = (done && STANDARD_SHIPPING > 0)
      ? '<em class="ls-meter-save">' + money(STANDARD_SHIPPING) + ' saved</em>'
      : '';

    meter.innerHTML =
      '<div class="ls-meter-txt' + (done ? ' done' : '') + '">' +
        icon(done ? 'check' : 'truck', 'ls-ic') + '<span>' + txt + '</span>' + saved +
      '</div>' +
      '<div class="ls-bar"><i class="' + (done ? 'done' : '') + '" style="width:' + pct + '%"></i></div>';
  }

  /* Two rows, not three. The RRP used to stack under the current price in a
     right-hand column, which made every line taller than it needed to be and
     stranded the quantity pill in white space. Current price now sits on the
     title line where the eye already is; the RRP drops down level with the
     pill; Remove takes the track between them. */
  function renderItem(item) {
    var was = lineWas(item);
    var strike = was > item.final_line_price
      ? '<s class="ls-it-w">' + money(was) + '</s>'
      : '';

    var variant = '';
    if (item.variant_title && item.variant_title !== 'Default Title') variant = item.variant_title;
    else if (item.product_type) variant = item.product_type;

    var name = esc(item.product_title);

    return '<div class="ls-it" data-key="' + esc(item.key) + '">' +
      '<div class="ls-th"><img src="' + esc(thumb(item.image, 160)) + '" alt="" width="58" height="58" decoding="async"></div>' +
      '<div class="ls-it-m">' +
        '<div class="ls-it-top">' +
          '<div><a class="ls-it-n" href="' + esc(item.url) + '">' + name + '</a>' +
          (variant ? '<div class="ls-it-v">' + esc(variant) + '</div>' : '') + '</div>' +
          '<div class="ls-it-p">' + money(item.final_line_price) + '</div>' +
        '</div>' +
        '<div class="ls-it-f">' +
          '<div class="ls-q">' +
            '<button type="button" data-act="dec" aria-label="Decrease quantity of ' + name + '">' + icon('minus') + '</button>' +
            '<span>' + item.quantity + '</span>' +
            '<button type="button" data-act="inc" aria-label="Increase quantity of ' + name + '">' + icon('plus') + '</button>' +
          '</div>' +
          '<button type="button" class="ls-rm" data-act="rm" aria-label="Remove ' + name + '">Remove</button>' +
          strike +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function renderGift(item) {
    var was = lineWas(item);
    var strike = was > 0 ? '<s class="ls-gift-w">' + money(was) + '</s>' : '';
    return '<div class="ls-gift">' +
      '<div class="ls-gift-th"><img src="' + esc(thumb(item.image, 120)) + '" alt="" width="38" height="38" decoding="async"></div>' +
      '<div class="ls-gift-i">' +
        '<div class="ls-gift-n">' + esc(item.product_title) + '</div>' +
        '<div class="ls-gift-s">Your welcome gift, added automatically</div>' +
      '</div>' +
      '<div class="ls-gift-r">' + strike + '<span class="ls-gift-p">Free</span></div>' +
    '</div>';
  }

  function renderUpsells() {
    var giftOnly = isGiftOnly();
    var max = giftOnly ? (C.maxUpsellsGift || 3) : (C.maxUpsells || 2);
    var list = pickUpsells(max);
    if (!list.length) return '';

    var gap = shippingGap();
    var crossesAny = !giftOnly && list.some(function (p) { return gap > 0 && p.price >= gap; });

    var heading = giftOnly
      ? 'Pair your gift with'
      : (crossesAny ? 'Add ' + money(gap) + ' and shipping is on us' : 'Frequently added');

    var rows = list.map(function (p) {
      var crosses = !giftOnly && gap > 0 && p.price >= gap;
      var sub = crosses ? 'Gets you over the <b>free shipping</b> line' : esc(p.sub);
      return '<div class="ls-up">' +
        '<div class="ls-up-th"><img src="' + esc(thumb(p.image, 120)) + '" alt="" width="38" height="38" decoding="async"></div>' +
        '<div class="ls-up-i"><div class="ls-up-n">' + esc(p.title) + '</div>' +
        '<div class="ls-up-s">' + sub + '</div></div>' +
        '<button type="button" class="ls-up-b" data-act="add" data-id="' + p.variantId + '">' +
          icon('plus') + 'Add ' + money(p.price) +
        '</button>' +
      '</div>';
    }).join('');

    return '<div class="ls-up-h">' + heading + '</div>' + rows;
  }

  function renderEmpty() {
    body.innerHTML = renderError() +
      '<div class="ls-empty">' +
        '<div class="ls-empty-ic">' + icon('cart') + '</div>' +
        "<h3>Your cart's empty</h3>" +
        '<p>Keep food fresh up to 5&times; longer.<br>Free shipping on orders over ' + money(C.freeShippingThreshold) + '.</p>' +
        '<a class="ls-cta" href="' + esc(C.emptyCtaUrl) + '">Shop Bundles</a>' +
        benefits() +
      '</div>';
    foot.innerHTML = '';
  }

  function benefits() {
    return '<div class="ls-bens">' +
      '<div class="ls-ben"><div class="ls-ben-ic tp">' + icon('trustpilot') + '</div>' +
        '<div class="ls-ben-t">Excellent</div><div class="ls-ben-s">Rated ' + esc(C.rating) + '</div></div>' +
      '<div class="ls-ben"><div class="ls-ben-ic">' + icon('shield') + '</div>' +
        '<div class="ls-ben-t">1 Year</div><div class="ls-ben-s">Warranty</div></div>' +
      '<div class="ls-ben"><div class="ls-ben-ic">' + icon('truck') + '</div>' +
        '<div class="ls-ben-t">2-5 Days</div><div class="ls-ben-s">Nationwide</div></div>' +
    '</div>';
  }

  function protectionHTML() {
    if (!C.protection.variantId) return '';
    var on = !!protectionLine();
    return '<div class="ls-prot">' +
      icon('shield', 'ls-prot-ic') +
      '<div class="ls-prot-i">' +
        '<div class="ls-prot-n" id="ls-prot-n">' + esc(C.protection.title) +
          '<button type="button" class="ls-prot-q" data-act="protinfo"' +
            ' aria-expanded="' + (protInfoOpen ? 'true' : 'false') + '"' +
            ' aria-controls="ls-prot-info"' +
            ' aria-label="What is ' + esc(C.protection.title) + '?">?</button>' +
        '</div>' +
        '<div class="ls-prot-s" id="ls-prot-s">' + esc(C.protection.sub) + ' ' + money(C.protection.price) + '</div>' +
      '</div>' +
      '<label class="ls-sw"><input type="checkbox" data-act="prot"' + (on ? ' checked' : '') +
        ' aria-labelledby="ls-prot-n" aria-describedby="ls-prot-s"><i></i></label>' +
    '</div>' +
    (protInfoOpen
      ? '<div class="ls-prot-info" id="ls-prot-info">' + esc(C.protection.tooltip) + '</div>'
      : '');
  }

  /* The footer is built ONCE and then patched in place. Rebuilding it with
     innerHTML destroyed and recreated the Checkout button, and a tap landing
     while that happened was silently dropped. The node must survive. */
  function buildFooterSkeleton() {
    foot.innerHTML =
      '<div class="ls-prot-wrap" data-f="prot"></div>' +
      '<div class="ls-sums">' +
        '<div class="ls-sum"><span>Subtotal</span><b data-f="sub"></b></div>' +
        '<div class="ls-sum save" data-f="saverow" hidden><span>You save</span><b data-f="save"></b></div>' +
        '<div class="ls-sum ship" data-f="shiprow" hidden><span>Shipping</span><b data-f="ship"></b></div>' +
        '<div class="ls-sum tot"><span class="l">Total</span><span class="v" data-f="tot"></span></div>' +
      '</div>' +
      '<button type="button" class="ls-cta" data-act="checkout">' +
        icon('lock') + '<span data-f="cta"></span>' +
      '</button>' +
      benefits() +
      '<div class="ls-foot-note">30-day money back guarantee &middot; Buy Now, Pay Later available</div>';
  }

  function renderFooter() {
    var cart = state.cart;

    if (isGiftOnly()) {
      foot.innerHTML =
        '<a class="ls-cta" href="' + esc(C.emptyCtaUrl) + '">Shop Bundles</a>' +
        '<div class="ls-cta-note">Your gift is saved and travels with your order.</div>' +
        benefits() +
        '<div class="ls-foot-note">30-day money back guarantee &middot; Buy Now, Pay Later available</div>';
      return;
    }

    if (!foot.querySelector('[data-act="checkout"]')) buildFooterSkeleton();

    var protSlot = foot.querySelector('[data-f="prot"]');
    var nextProt = protectionHTML();
    if (protSlot && protSlot.innerHTML !== nextProt) protSlot.innerHTML = nextProt;

    // The figures are known now, so un-hide the rows renderInstantFooter hid.
    var sums = foot.querySelector('.ls-sums');
    if (sums && sums.style.display === 'none') sums.style.display = '';

    /* Subtotal is the RRP total and Total is what they pay, so the rows read
       as arithmetic: subtotal minus saving equals total. Deriving the saving
       from original_total_price instead made it disappear whenever no discount
       was running, even though the RRP saving was real. */
    var rrp = rrpSubtotal();
    var saved = Math.max(0, rrp - cart.total_price);

    var set = function (key, val) {
      var el = foot.querySelector('[data-f="' + key + '"]');
      if (el && el.textContent !== val) el.textContent = val;
    };
    set('sub', money(rrp));
    set('tot', money(cart.total_price));
    set('cta', ' Checkout · ' + money(cart.total_price));

    var saveRow = foot.querySelector('[data-f="saverow"]');
    if (saveRow) {
      if (saved > 0) { saveRow.hidden = false; set('save', '−' + money(saved)); }
      else saveRow.hidden = true;
    }

    /* Shipping is stated ONLY when it is free. Quoting R99 below the threshold
       would introduce a cost into a summary the customer is reading to decide
       whether to continue, and the meter above already tells them what is
       needed to remove it. */
    var shipRow = foot.querySelector('[data-f="shiprow"]');
    if (shipRow) {
      if (cart.items.length && shippingGap() === 0) {
        shipRow.hidden = false;
        set('ship', 'FREE');
      } else {
        shipRow.hidden = true;
      }
    }
  }

  function render() {
    var cart = state.cart;
    if (!cart) { if (state.pending) renderPending(); return; }

    var n = cart.items.reduce(function (s, i) {
      return isProtection(i) ? s : s + i.quantity;
    }, 0);
    count.textContent = n === 1 ? '(1 item)' : '(' + n + ' items)';

    if (!cart.items.length) { renderMeter(); renderEmpty(); syncBubble(0); return; }

    var html = renderError();
    cart.items.forEach(function (i) {
      if (isProtection(i)) return;
      html += isGift(i) ? renderGift(i) : renderItem(i);
    });
    html += renderUpsells();

    // Preserve scroll position: a full body rebuild otherwise snaps the list
    // back to the top after every quantity change.
    var st = body.scrollTop;
    body.innerHTML = html;
    body.scrollTop = st;

    renderMeter();
    renderFooter();
    syncBubble(n);
  }

  function syncBubble(n) {
    var link = document.getElementById('cart-icon-bubble')
      || document.querySelector('a[href$="/cart"]');
    var bubble = document.querySelector('.cart-count-bubble');

    if (!bubble && link && n > 0) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      link.appendChild(bubble);
    }
    if (!bubble) return;

    var vis = bubble.querySelector('span[aria-hidden]');
    if (vis) vis.textContent = n;
    var sr = bubble.querySelector('.visually-hidden');
    if (sr) sr.textContent = n === 1 ? '1 item' : n + ' items';
    bubble.style.display = n > 0 ? '' : 'none';
  }

  /* ---------- mutations ---------- */

  function busy(on) {
    state.busy = on;
    dw.classList.toggle('busy', on);
  }

  function mutate(fn) {
    if (state.busy) return Promise.resolve();
    busy(true);
    state.error = null;
    return Promise.resolve()
      .then(fn)
      .then(function () { return refresh(); })
      .catch(function (e) {
        console.error('[lsCart]', e);
        state.error = e && e.userMessage ? e.userMessage : 'Something went wrong. Please try again.';
        return refresh(true).catch(function () { render(); });
      })
      .finally(function () { busy(false); });
  }

  function refresh(keepError) {
    return api(C.routes.cart).then(function (c) {
      state.cart = c;
      state.pending = false;
      if (!keepError) state.error = null;
      if (!c.items.length) resetProtectionState();
      render();
      if (window.lsCartOnUpdate) { try { window.lsCartOnUpdate(c); } catch (e) {} }
      setTimeout(maybeAutoProtection, 0);
      setTimeout(fillCompareGaps, 0);
      return c;
    });
  }

  function changeLine(key, qty) {
    return mutate(function () {
      return jsonPost(C.routes.cartChange, { id: key, quantity: qty });
    });
  }

  function add(id, qty) {
    return mutate(function () {
      return jsonPost(C.routes.cartAdd, { items: [{ id: Number(id), quantity: qty || 1 }] });
    });
  }

  /* Rendering Checkout before the cart is confirmed is only safe if tapping it
     cannot outrun the add. If a request is in flight we wait for it, then go. */
  function goCheckout() {
    if (!inflight) { window.location = '/checkout'; return; }
    var cta = foot.querySelector('[data-f="cta"]');
    if (cta) cta.textContent = ' One moment…';
    inflight.then(function () {
      window.location = '/checkout';
    }).catch(function () {
      if (cta) cta.textContent = ' Checkout';
    });
  }

  /* ---------- open / close ---------- */

  function reveal() {
    state.open = true;
    ov.classList.add('on');
    dw.classList.add('on');
    document.body.classList.add('ls-locked');
    dw.setAttribute('aria-hidden', 'false');
  }

  function open() {
    state.error = null;
    reveal();
    if (state.cart) render(); else { state.pending = true; renderPending(); }
    refresh().catch(function (e) {
      state.pending = false;
      state.error = e && e.userMessage ? e.userMessage : 'We could not load your cart. Please refresh.';
      render();
    });
  }

  function close() {
    state.open = false;
    protInfoOpen = false;
    ov.classList.remove('on');
    dw.classList.remove('on');
    document.body.classList.remove('ls-locked');
    dw.setAttribute('aria-hidden', 'true');
  }

  /* ---------- wiring ---------- */

  function bind() {
    ov.addEventListener('click', close);

    document.addEventListener('click', function (e) {
      if (!state.open) return;
      if (dw.contains(e.target)) return;
      if (window.lsCartIsThirdParty && window.lsCartIsThirdParty(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      close();
    }, true);

    dw.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-act]');

      // Safety net: if a re-render swapped the button out from under the tap,
      // the click retargets to a container with no [data-act].
      if (!btn && foot.contains(e.target) && !e.target.closest('.ls-sw,.ls-prot')) {
        var cta = foot.querySelector('[data-act="checkout"]');
        if (cta && !isGiftOnly()) { goCheckout(); return; }
      }
      if (!btn) return;
      var act = btn.getAttribute('data-act');

      if (act === 'close') return close();
      if (act === 'checkout') { goCheckout(); return; }
      if (act === 'add') return add(btn.getAttribute('data-id'), 1);

      if (act === 'protinfo') {
        protInfoOpen = !protInfoOpen;
        var slot = foot.querySelector('[data-f="prot"]');
        if (slot) slot.innerHTML = protectionHTML();
        return;
      }

      if (act === 'prot') {
        if (protectionLine()) {
          setProtOptOut(true);
          autoProtTried = true;
          protAttempts = 0;
          return setProtection(false);
        }
        setProtOptOut(false);
        protAttempts = 0;
        return setProtection(true);
      }

      var row = btn.closest('[data-key]');
      if (!row) return;
      var key = row.getAttribute('data-key');

      var live = (state.cart && state.cart.items.filter(function (i) { return i.key === key; })[0]) || null;
      if (!live) { return refresh(); }
      var qty = live.quantity;

      if (act === 'inc') return changeLine(key, qty + 1);
      if (act === 'dec') return changeLine(key, Math.max(0, qty - 1));
      if (act === 'rm') return changeLine(key, 0);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.open) close();
    });

    document.addEventListener('click', function (e) {
      var a = e.target.closest('a');
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
      if (a.hasAttribute('data-ls-ignore')) return;

      var path;
      try { path = new URL(a.href, window.location.origin); } catch (err) { return; }
      if (path.origin !== window.location.origin) return;
      if (!/^\/cart\/?$/.test(path.pathname)) return;

      e.preventDefault();
      open();
    });

    /* Add to Cart. The drawer opens and a real Checkout button is painted on
       this tick, before the request is even sent. Delivery Protection rides
       along in the same request. */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      e.preventDefault();
      e.stopPropagation();

      if (state.busy) return;

      var item;
      try { item = formToItem(form); } catch (err) { return; }
      if (!item.id) return;

      var items = [item];
      var folded = false;
      if (wantsProtectionNow()) {
        items.push({ id: Number(C.protection.variantId), quantity: 1 });
        folded = true;
      }

      busy(true);
      state.error = null;
      reveal();
      renderAdding();

      inflight = jsonPost(C.routes.cartAdd, { items: items })
        .catch(function (err) {
          if (!folded) throw err;
          // add.js is atomic: if Delivery Protection is unavailable for any
          // reason it takes the real product down with it. Never let a R29
          // upsell block the sale - retry with the product alone.
          console.warn('[lsCart] protection add failed, retrying product alone', err);
          return jsonPost(C.routes.cartAdd, { items: [item] });
        })
        .then(function () { return refresh(); })
        .catch(function (err) {
          console.error('[lsCart]', err);
          state.error = err && err.userMessage ? err.userMessage : 'We could not add that. Please try again.';
          state.pending = false;
          return refresh(true)
            .catch(function () { render(); })
            .then(function () { throw err; });
        })
        .finally(function () {
          busy(false);
          inflight = null;
          setTimeout(maybeAutoProtection, 0);
        });

      inflight.catch(function () {});
    }, true);
  }

  function init() {
    dw = document.getElementById('ls-cart');
    ov = document.getElementById('ls-cart-ov');
    if (!dw || !ov) return;
    body = dw.querySelector('.ls-body');
    foot = dw.querySelector('.ls-ft');
    meter = dw.querySelector('.ls-meter');
    count = dw.querySelector('.ls-hd-t span');

    loadCompareCache();
    bind();

    /* Liquid already rendered the cart into the page, so asking the server for
       it again on every pageview was a round trip spent learning nothing. */
    var seed = window.LS_CART_SEED;
    if (seed && seed.items) {
      state.cart = seed;
      render();
      setTimeout(fillCompareGaps, 0);
    } else {
      refresh().catch(function (e) { console.error('[lsCart]', e); });
    }

    window.lsCart = { open: open, close: close, refresh: refresh, add: add, state: state };
    window.upcartOpenCart = open;
    document.dispatchEvent(new CustomEvent('ls:cart:ready'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
